// Pure helpers for payment destinations + salary allocation (Increment B).
// Percentages travel as BASIS POINTS (1% = 100 bp) so 33.33% is exact — the
// server stores and validates the same integers.

export const FULL_BP = 10000;
export const MAX_SPLIT_LINES = 3;

/** "33.33" → 3333. null for anything the server would refuse (≤0, >100, >2 dp). */
export function percentToBP(input: string): number | null {
  const s = (input ?? '').trim();
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(s)) return null;
  const [whole, frac = ''] = s.split('.');
  const bp = Number(whole) * 100 + Number((frac + '00').slice(0, 2));
  if (!Number.isFinite(bp) || bp <= 0 || bp > FULL_BP) return null;
  return bp;
}

export function bpToPercent(bp: number): string {
  return `${Math.floor(bp / 100)}.${String(bp % 100).padStart(2, '0')}`;
}

export type AllocationTotalsState = 'ok' | 'under' | 'over' | 'invalid' | 'single' | 'too_many';

export interface AllocationTotals {
  state: AllocationTotalsState;
  sumBP: number;
  /** FULL_BP − sum (negative when over). */
  remainingBP: number;
}

/** Live validation of the shares being typed. One line is not a split. */
export function allocationTotals(percents: string[]): AllocationTotals {
  const bps = percents.map(percentToBP);
  const sumBP = bps.reduce<number>((a, b) => a + (b ?? 0), 0);
  const remainingBP = FULL_BP - sumBP;
  let state: AllocationTotalsState;
  if (percents.length === 1) state = 'single';
  else if (percents.length > MAX_SPLIT_LINES) state = 'too_many';
  else if (bps.some((b) => b === null)) state = 'invalid';
  else if (sumBP === FULL_BP) state = 'ok';
  else state = sumBP < FULL_BP ? 'under' : 'over';
  return { state, sumBP, remainingBP };
}

/**
 * Preview of how an amount (in whole currency units) splits: each line gets
 * floor(amount × bp / 10000) and the rounding remainder goes to the FIRST line,
 * so the parts always sum to the amount exactly. Mirrors payroll's rule.
 */
export function previewSplit(amount: number, bps: number[]): number[] {
  const parts = bps.map((bp) => Math.floor((amount * bp) / FULL_BP));
  const rest = amount - parts.reduce((a, b) => a + b, 0);
  if (parts.length > 0) parts[0] += rest;
  return parts;
}

export type DestinationsState = 'loading' | 'error' | 'unavailable' | 'empty' | 'ok';

/** Four honest states: an outage or an empty list must never read as "all clear". */
export function destinationsState(input: {
  loading: boolean;
  error?: boolean;
  status?: string;
  destinations?: unknown[] | null;
}): DestinationsState {
  if (input.loading) return 'loading';
  if (input.error) return 'error';
  if (input.status !== 'ok' || !input.destinations) return 'unavailable';
  return input.destinations.length === 0 ? 'empty' : 'ok';
}
